'use client'

import { useRef } from 'react'
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion'

type WordProps = {
  children: string
  index: number
  total: number
  progress: MotionValue<number>
  reduceMotion: boolean | null
}

function Word({ children, index, total, progress, reduceMotion }: WordProps) {
  const start = index / Math.max(total, 1)
  const end = Math.min(start + 0.18, 1)
  const opacity = useTransform(progress, [start, end], reduceMotion ? [1, 1] : [0.14, 1])
  const y = useTransform(progress, [start, end], reduceMotion ? [0, 0] : [24, 0])
  const filter = useTransform(
    progress,
    [start, end],
    reduceMotion ? ['blur(0px)', 'blur(0px)'] : ['blur(9px)', 'blur(0px)'],
  )

  return (
    <motion.span style={{ opacity, y, filter }} className="inline-block">
      {children}
    </motion.span>
  )
}

export default function ScrollWordReveal({
  text,
  className = '',
}: {
  text: string
  className?: string
}) {
  const ref = useRef<HTMLParagraphElement>(null)
  const reduceMotion = useReducedMotion()
  const words = text.trim().split(/\s+/)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.88', 'end 0.3'],
  })

  return (
    <p ref={ref} className={className}>
      {words.map((word, index) => (
        <span key={`${word}-${index}`} className="me-[0.28em] inline-block overflow-hidden align-bottom">
          <Word
            index={index}
            total={words.length}
            progress={scrollYProgress}
            reduceMotion={reduceMotion}
          >
            {word}
          </Word>
        </span>
      ))}
    </p>
  )
}
